import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Silence,
  CreateSilenceRequest,
  SilenceListParams,
  silenceService,
} from '../../../../api/cansense/silenceApi';

interface UseSilencesReturn {
  silences: Silence[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createSilence: (data: CreateSilenceRequest) => Promise<Silence>;
  deleteSilence: (id: number) => Promise<void>;
  quickSilence: (
    exporterId: number,
    durationMinutes: number,
    comment: string
  ) => Promise<Silence>;
  extendSilence: (id: number, durationMinutes: number) => Promise<Silence>;
}

/**
 * Hook for managing alert silences
 *
 * @param filters Optional filters for the silence list
 * @param filters.status Filter by status: 'active', 'expired', or 'all'
 * @param filters.exporterId Filter by specific exporter ID
 */
export const useSilences = (filters?: SilenceListParams): UseSilencesReturn => {
  const [silences, setSilences] = useState<Silence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchSilences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await silenceService.list(filters);
      if (mountedRef.current) {
        setSilences(data);
      }
    } catch (err) {
      if (mountedRef.current) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch silences';
        setError(errorMessage);
        console.error('Failed to fetch silences:', err);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [filters?.status, filters?.exporterId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchSilences();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchSilences]);

  const createSilence = useCallback(
    async (data: CreateSilenceRequest): Promise<Silence> => {
      try {
        const newSilence = await silenceService.create(data);
        if (mountedRef.current) {
          setSilences((prev) => [...prev, newSilence]);
        }
        return newSilence;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create silence';
        throw new Error(errorMessage);
      }
    },
    []
  );

  const deleteSilence = useCallback(async (id: number): Promise<void> => {
    try {
      await silenceService.delete(id);
      if (mountedRef.current) {
        setSilences((prev) => prev.filter((s) => s.id !== id));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete silence';
      throw new Error(errorMessage);
    }
  }, []);

  const quickSilence = useCallback(
    async (
      exporterId: number,
      durationMinutes: number,
      comment: string
    ): Promise<Silence> => {
      try {
        const newSilence = await silenceService.quickSilence(
          exporterId,
          durationMinutes,
          comment
        );
        if (mountedRef.current) {
          setSilences((prev) => [...prev, newSilence]);
        }
        return newSilence;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create quick silence';
        throw new Error(errorMessage);
      }
    },
    []
  );

  const extendSilence = useCallback(
    async (id: number, durationMinutes: number): Promise<Silence> => {
      try {
        const updatedSilence = await silenceService.extend(id, durationMinutes);
        if (mountedRef.current) {
          setSilences((prev) =>
            prev.map((s) => (s.id === id ? updatedSilence : s))
          );
        }
        return updatedSilence;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to extend silence';
        throw new Error(errorMessage);
      }
    },
    []
  );

  return {
    silences,
    loading,
    error,
    refetch: fetchSilences,
    createSilence,
    deleteSilence,
    quickSilence,
    extendSilence,
  };
};
